import React__default, { lazy, useRef, useState, useCallback, useMemo, useEffect, Suspense } from 'react';
import clsx from 'clsx';
import { TextInputV2 } from '../TextInputV2.js';
import { isEqual, debounce } from 'lodash-es';

const ValidationMessage = lazy(() => import('./ValidationMessage.js'));
const ValidatedTextInputV2 = ({ id, validators, validationEnabled = true, initialValidation, showOnlyOneError, forceValidate, showValidationMessage = true, delay, floatMessage = false, onChange, onBlur, onFocus, onValidate, value: controlledValue, className, ...inputProps }) => {
    // assign an id to each validator (when they change reassign that id)
    // based on the id we can set which validator messages we want to show
    // this is so we can send validator messages as node elements (mostly for translations)
    const ranValidation = useRef(false);
    const [validationResult, setValidationResult] = useState({
        messages: [],
        numError: 0,
        numSuccess: 0
    });
    const runValidation = useCallback((value) => {
        ranValidation.current = true;
        const successValidators = [];
        const failedValidators = [];
        let numSuccess = 0;
        let numError = 0;
        const messages = [];
        validators?.every((v) => {
            if (v !== undefined) {
                const validationResult = v.validator?.(value);
                if (['error', false].includes(validationResult)) {
                    // returning 'error' is legacy. Should use boolean.
                    failedValidators.push(v);
                    if (v.errorMessage) {
                        numError += 1;
                        messages.push({
                            ...v,
                            isError: true
                        });
                    }
                    if (showOnlyOneError) {
                        // Break out of every loop
                        return false;
                    }
                }
                else {
                    successValidators.push(v);
                    if (v.successMessage) {
                        numSuccess += 1;
                        messages.push({
                            ...v,
                            isError: false
                        });
                    }
                }
            }
            // Continue loop
            return true;
        });
        setValidationResult((prev) => {
            const newResult = {
                numError,
                numSuccess,
                messages
            };
            if (isEqual(prev, newResult)) {
                return prev;
            }
            onValidate?.(failedValidators, successValidators);
            return newResult;
        });
        return !numError;
    }, [onValidate, showOnlyOneError, validators]);
    const onChangeInput = useCallback((v, e) => {
        onChange?.(v, e, validationEnabled, validationEnabled ? runValidation(v) : true);
    }, [onChange, runValidation, validationEnabled]);
    const debounceOnChange = useMemo(() => debounce((v, e) => {
        onChangeInput?.(v, e);
    }, delay, { leading: false, trailing: true }), [delay, onChangeInput]);
    const handleOnChange = useCallback((v, e) => {
        delay ? debounceOnChange(v, e) : onChangeInput?.(v, e);
    }, [debounceOnChange, delay, onChangeInput]);
    const onBlurInput = useCallback((e) => {
        const v = e.target.value;
        onBlur?.(v, e, validationEnabled, validationEnabled ? runValidation(v) : true);
    }, [onBlur, runValidation, validationEnabled]);
    const onFocusInput = useCallback((e) => {
        const v = e.target.value;
        onFocus?.(v, e, validationEnabled, validationEnabled ? runValidation(v) : true);
    }, [onFocus, runValidation, validationEnabled]);
    const canValidateControlledValue = useMemo(() => validationEnabled && controlledValue !== undefined, [controlledValue, validationEnabled]);
    // Validate again if prop changed and validation ran at least once.
    useEffect(() => {
        if (canValidateControlledValue && ranValidation.current) {
            runValidation(controlledValue.toString());
        }
    }, [runValidation, forceValidate, canValidateControlledValue, controlledValue]);
    useEffect(() => {
        if (initialValidation && canValidateControlledValue) {
            // run validation at mount
            runValidation(controlledValue.toString());
        }
        // Note: Only run once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        return () => {
            debounceOnChange.cancel();
        };
    }, [debounceOnChange]);
    const classPrefix = 'c-input-validation';
    return (React__default.createElement("div", { "data-testid": id ? `${id}-validation-wrapper` : undefined, className: validationEnabled
            ? clsx(classPrefix, {
                [`${classPrefix}--error`]: !!validationResult.numError,
                [`${classPrefix}--success`]: !!validationResult.numSuccess && !validationResult.numError
            }, className)
            : undefined },
        React__default.createElement(TextInputV2, { ...inputProps, id: id, value: controlledValue, onChange: onChange ? handleOnChange : undefined, onBlur: onBlur ? onBlurInput : undefined, onFocus: onFocus ? onFocusInput : undefined, removeWrapper: true }),
        React__default.createElement(Suspense, { fallback: '' }, showValidationMessage && !!validationResult.messages.length && (React__default.createElement(ValidationMessage, { floatMessage: floatMessage, messages: validationResult.messages })))));
};

export { ValidatedTextInputV2 };
//# sourceMappingURL=ValidatedTextInputV2.js.map
